/**
 * Presentation model for the clinician-facing patient demographics view.
 *
 * The backend returns the stored profile as raw controlled values (`"175"`,
 * `"ecigaretteFormer"`, `["copd", "other"]`). This module owns the mapping from
 * those values to the grouped, localised, unit-carrying rows the dashboard
 * shows, so the overlay component stays presentational and the mapping stays
 * testable.
 */

/**
 * Controlled option values the patient app stores, mapped to their translation
 * keys. Unknown values fall through to the raw stored value rather than being
 * hidden, so a value added by the patient app before the dashboard catches up
 * is still visible to a clinician.
 */
const OPTION_LABEL_KEYS = {
  sexAtBirth: {
    Female: "profilePage.demographicsSexFemale",
    Male: "profilePage.demographicsSexMale",
  },
  ethnicity: {
    "White - British": "profilePage.demographicsEthnicityWhiteBritish",
    "White - Irish": "profilePage.demographicsEthnicityWhiteIrish",
    "White - Any other White background":
      "profilePage.demographicsEthnicityWhiteOther",
    "Mixed - White and Black Caribbean":
      "profilePage.demographicsEthnicityMixedWhiteBlackCaribbean",
    "Mixed - White and Black African":
      "profilePage.demographicsEthnicityMixedWhiteBlackAfrican",
    "Mixed - White and Asian":
      "profilePage.demographicsEthnicityMixedWhiteAsian",
    "Mixed - Any other mixed background":
      "profilePage.demographicsEthnicityMixedOther",
    "Asian or Asian British - Indian":
      "profilePage.demographicsEthnicityAsianIndian",
    "Asian or Asian British - Pakistani":
      "profilePage.demographicsEthnicityAsianPakistani",
    "Asian or Asian British - Bangladeshi":
      "profilePage.demographicsEthnicityAsianBangladeshi",
    "Asian or Asian British - Any other Asian background":
      "profilePage.demographicsEthnicityAsianOther",
    "Black or Black British - Caribbean":
      "profilePage.demographicsEthnicityBlackCaribbean",
    "Black or Black British - African":
      "profilePage.demographicsEthnicityBlackAfrican",
    "Black or Black British - Any other Black background":
      "profilePage.demographicsEthnicityBlackOther",
    "Other Ethnic Groups - Chinese":
      "profilePage.demographicsEthnicityChinese",
    "Other Ethnic Groups - Any other ethnic group":
      "profilePage.demographicsEthnicityOtherEthnicGroup",
    "Not stated": "profilePage.demographicsEthnicityNotStated",
    "Not known": "profilePage.demographicsEthnicityNotKnown",
    Caucasian: "profilePage.demographicsEthnicityCaucasian",
    Black: "profilePage.demographicsEthnicityBlack",
    "North East Asian": "profilePage.demographicsEthnicityNorthEastAsian",
    "South East Asian": "profilePage.demographicsEthnicitySouthEastAsian",
    Other: "profilePage.demographicsEthnicityOther",
  },
  respiratoryConditions: {
    none: "profilePage.demographicsConditionNone",
    ild: "profilePage.demographicsConditionIld",
    copd: "profilePage.demographicsConditionCopd",
    asthma: "profilePage.demographicsConditionAsthma",
    other: "profilePage.demographicsConditionOther",
  },
  smoking: {
    never: "profilePage.demographicsSmokingNever",
    former: "profilePage.demographicsSmokingFormer",
    ecigaretteFormer: "profilePage.demographicsSmokingEcigaretteFormer",
    current: "profilePage.demographicsSmokingCurrent",
    ecigarette: "profilePage.demographicsSmokingEcigarette",
  },
  yearsSinceQuit: {
    lessThan1month: "profilePage.demographicsQuitLessThan1Month",
    "1-6months": "profilePage.demographicsQuit1To6Months",
    "6-12months": "profilePage.demographicsQuit6To12Months",
    "1-5years": "profilePage.demographicsQuit1To5Years",
    moreThan5years: "profilePage.demographicsQuitMoreThan5Years",
  },
};

/**
 * Field definitions in display order, grouped into the sections a clinician
 * reads them in: who the patient is, what their body measurements are, and the
 * respiratory background that shapes their expected values.
 *
 * `type` drives formatting and `unit` is appended to numeric values.
 *
 * `dependent: true` marks a follow-up field the patient is only ever asked when
 * an earlier answer calls for it (the "other" free-text boxes, the smoking
 * follow-ups). Those rows are dropped when unanswered — a permanent
 * "Not provided" against a question the patient was never asked reads as a gap
 * in their record when it is nothing of the sort.
 *
 * Labels carrying `{{site}}` / `{{subject}}` are resolved through the wording
 * translator, so an organisation configured for research reads "Study" where a
 * clinical one reads "Clinic".
 */
const DEMOGRAPHIC_GROUPS = [
  {
    key: "identity",
    titleKey: "profilePage.demographicsIdentityTitle",
    titleFallback: "Identity",
    fields: [
      { key: "subjectId", labelKey: "profilePage.demographicsSubjectId", labelFallback: "Participant ID", type: "text" },
      { key: "clinicStudyId", labelKey: "profilePage.demographicsClinicStudyId", labelFallback: "{{site}}", type: "text" },
      { key: "dateOfBirth", labelKey: "profilePage.demographicsDateOfBirth", labelFallback: "Date of birth", type: "date" },
      { key: "sexAtBirth", labelKey: "profilePage.demographicsSexAtBirth", labelFallback: "Sex at birth", type: "option" },
      { key: "ethnicity", labelKey: "profilePage.demographicsEthnicity", labelFallback: "Ethnicity", type: "option" },
      { key: "ethnicityOther", labelKey: "profilePage.demographicsEthnicityOtherDetail", labelFallback: "Ethnicity (other)", type: "text", dependent: true },
    ],
  },
  {
    key: "measurements",
    titleKey: "profilePage.demographicsMeasurementsTitle",
    titleFallback: "Body measurements",
    fields: [
      { key: "height", labelKey: "profilePage.demographicsHeight", labelFallback: "Height", type: "number", unit: "cm" },
      { key: "weight", labelKey: "profilePage.demographicsWeight", labelFallback: "Weight", type: "number", unit: "kg" },
      { key: "chestCircumference", labelKey: "profilePage.demographicsChestCircumference", labelFallback: "Chest circumference", type: "number", unit: "cm" },
    ],
  },
  {
    key: "respiratory",
    titleKey: "profilePage.demographicsRespiratoryTitle",
    titleFallback: "Respiratory background",
    fields: [
      { key: "respiratoryConditions", labelKey: "profilePage.demographicsRespiratoryConditions", labelFallback: "Respiratory conditions", type: "optionList" },
      { key: "respiratoryConditionsOther", labelKey: "profilePage.demographicsRespiratoryConditionsOther", labelFallback: "Condition (other)", type: "text", dependent: true },
      { key: "smoking", labelKey: "profilePage.demographicsSmoking", labelFallback: "Smoking status", type: "option" },
      { key: "yearsSinceQuit", labelKey: "profilePage.demographicsYearsSinceQuit", labelFallback: "Time since quitting", type: "option", dependent: true },
      { key: "exQuitEffect", labelKey: "profilePage.demographicsExQuitEffect", labelFallback: "Quitting effect on breathing (0–10)", type: "text", dependent: true },
    ],
  },
];

/** Every demographic field key in display order. */
export const DEMOGRAPHIC_FIELD_KEYS = DEMOGRAPHIC_GROUPS.flatMap((group) =>
  group.fields.map((field) => field.key),
);

function isEmptyValue(value) {
  if (value === null || value === undefined) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return String(value).trim().length === 0;
}

function translateOption(fieldKey, value, t) {
  const labelKey = OPTION_LABEL_KEYS[fieldKey]?.[value];
  return labelKey ? t(labelKey, String(value)) : String(value);
}

/**
 * Formats one stored profile value for display.
 *
 * @param {{ key: string, type?: string, unit?: string }} field - Field definition.
 * @param {unknown} value - Raw stored value.
 * @param {(key: string, fallback?: string) => string} t - i18next translator.
 * @param {{ formatDate?: (isoDate: string) => string }} [options={}] - Optional date formatter.
 * @returns {string | null} Display text, or null when the patient never answered.
 */
export function formatDemographicValue(field, value, t, options = {}) {
  if (isEmptyValue(value)) {
    return null;
  }

  switch (field.type) {
    case "number": {
      const text = String(value).trim();
      return field.unit ? `${text} ${field.unit}` : text;
    }
    case "date":
      return options.formatDate ? options.formatDate(String(value)) : String(value);
    case "option":
      return translateOption(field.key, value, t);
    case "optionList": {
      const entries = Array.isArray(value) ? value : [value];
      return entries
        .map((entry) => translateOption(field.key, entry, t))
        .join(", ");
    }
    default:
      return String(value).trim();
  }
}

/**
 * Builds the grouped, display-ready demographic rows for one patient.
 *
 * Fields the patient never answered are kept (shown as a placeholder) so a
 * clinician can see *that* a value is missing rather than wondering whether the
 * dashboard simply does not support it.
 *
 * @param {Record<string, unknown> | null | undefined} profile - Stored profile payload.
 * @param {(key: string, fallback?: string) => string} t - i18next translator.
 * @param {{
 *   historyFields?: Record<string, unknown[]>,
 *   formatDate?: (isoDate: string) => string
 * }} [options={}] - History payload used to flag which rows have a timeline.
 * @returns {Array<{
 *   key: string,
 *   title: string,
 *   rows: Array<{
 *     key: string,
 *     label: string,
 *     value: string | null,
 *     rawValue: unknown,
 *     changeCount: number,
 *     hasHistory: boolean
 *   }>
 * }>} Grouped rows in display order.
 */
export function buildDemographicSections(profile, t, options = {}) {
  const source = profile && typeof profile === "object" ? profile : {};
  const historyFields =
    options.historyFields && typeof options.historyFields === "object"
      ? options.historyFields
      : null;

  return DEMOGRAPHIC_GROUPS.map((group) => ({
    key: group.key,
    title: t(group.titleKey, group.titleFallback),
    rows: group.fields
      .filter(
        (field) =>
          !field.dependent ||
          !isEmptyValue(source[field.key]) ||
          Boolean(historyFields?.[field.key]?.length),
      )
      .map((field) => {
        const rawValue = source[field.key] ?? null;
        const entries = Array.isArray(historyFields?.[field.key])
          ? historyFields[field.key]
          : [];

        return {
          key: field.key,
          label: t(field.labelKey, field.labelFallback),
          value: formatDemographicValue(field, rawValue, t, options),
          rawValue,
          changeCount: Math.max(entries.length - 1, 0),
          // Before the history payload has loaded we cannot know which fields
          // have a timeline, so every answered field stays clickable.
          hasHistory: historyFields
            ? entries.length > 0
            : !isEmptyValue(rawValue),
        };
      }),
  }));
}

/**
 * Builds the display-ready timeline for one field.
 *
 * @param {Record<string, unknown[]> | null | undefined} historyFields - History payload `fields` map.
 * @param {string} fieldKey - Field to read.
 * @param {(key: string, fallback?: string) => string} t - i18next translator.
 * @param {{ formatDate?: (isoDate: string) => string }} [options={}] - Optional date formatter.
 * @returns {Array<{
 *   key: string,
 *   recordedAt: string | null,
 *   value: string | null,
 *   isCurrent: boolean
 * }>} Entries newest first.
 */
export function buildDemographicFieldHistory(
  historyFields,
  fieldKey,
  t,
  options = {},
) {
  const field = findDemographicField(fieldKey);
  const entries = Array.isArray(historyFields?.[fieldKey])
    ? historyFields[fieldKey]
    : [];

  return entries.map((entry, index) => ({
    key: `${fieldKey}-${entry?.recordedAt || index}`,
    recordedAt: entry?.recordedAt || null,
    value: field ? formatDemographicValue(field, entry?.value, t, options) : null,
    isCurrent: Boolean(entry?.isCurrent),
  }));
}

/**
 * Looks up one field definition by key.
 *
 * @param {string} fieldKey - Field key.
 * @returns {{ key: string, labelKey: string, labelFallback: string, type: string, unit?: string } | null}
 */
export function findDemographicField(fieldKey) {
  for (const group of DEMOGRAPHIC_GROUPS) {
    const field = group.fields.find((candidate) => candidate.key === fieldKey);

    if (field) {
      return field;
    }
  }

  return null;
}

/**
 * Resolves the localised label for one field key.
 *
 * @param {string} fieldKey - Field key.
 * @param {(key: string, fallback?: string) => string} t - i18next translator.
 * @returns {string} Localised label, or the raw key when unknown.
 */
export function getDemographicFieldLabel(fieldKey, t) {
  const field = findDemographicField(fieldKey);
  return field ? t(field.labelKey, field.labelFallback) : String(fieldKey || "");
}

/**
 * Whether a profile payload contains at least one answered field.
 *
 * @param {Record<string, unknown> | null | undefined} profile - Stored profile payload.
 * @returns {boolean} True when the patient answered anything.
 */
export function hasAnyDemographicValue(profile) {
  const source = profile && typeof profile === "object" ? profile : {};
  return DEMOGRAPHIC_FIELD_KEYS.some((key) => !isEmptyValue(source[key]));
}
